import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Image as ImageIcon, Mic, Film, Paperclip, CheckCircle2,
  XCircle, Loader2, RotateCcw, X, Trash2, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadEvidenceFile, runPool } from "@/lib/uploadEvidence";
import { api } from "@/lib/apiClient";

const CONCURRENCY = 3;
const ANALYZABLE = ["photo", "pdf", "document", "voice", "audio"];

function iconFor(file) {
  const t = file.type || "";
  if (t.startsWith("image/")) return ImageIcon;
  if (t.startsWith("video/")) return Film;
  if (t.startsWith("audio/")) return Mic;
  if (t.includes("pdf") || t.includes("text") || t.includes("word")) return FileText;
  return Paperclip;
}
function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function BulkImport({ matterId, onClose, onDone }) {
  const [rows, setRows] = useState([]); // {id, file, status: queued|uploading|done|error, pct, error}
  const [running, setRunning] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const idRef = useRef(0);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setRows((prev) => [
      ...prev,
      ...incoming.map((file) => ({ id: `f${idRef.current++}`, file, status: "queued", pct: 0, error: null })),
    ]);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const clearDone = () => setRows((prev) => prev.filter((r) => r.status !== "done"));

  const upload = useCallback(async (targets) => {
    if (!targets.length) return;
    setRunning(true);
    const setRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    await runPool(targets, async (row) => {
      setRow(row.id, { status: "uploading", pct: 0, error: null });
      try {
        const doc = await uploadEvidenceFile(row.file, {
          matterId, onProgress: (pct) => setRow(row.id, { pct }),
        });
        if (doc?.import_job && doc?.job_id) {
          // ZIP archive → server-side background extraction; poll the import job.
          setRow(row.id, {
            status: "importing", pct: 100, isArchive: true, jobId: doc.job_id,
            archive: { discovered: doc.discovered || 0, processed: 0, added: 0, unsupported: 0, failed: 0, need_review: 0, percent: 0, status: "queued" },
          });
        } else {
          const analyzable = ANALYZABLE.includes(doc?.format);
          setRow(row.id, { status: "done", pct: 100, evId: doc?.id, analyzable, analysis: analyzable ? "analyzing" : null });
        }
      } catch (e) {
        setRow(row.id, { status: "error", error: e.response?.data?.detail || "Upload failed" });
      }
    }, CONCURRENCY);
    setRunning(false);
    onDone && onDone();
  }, [matterId, onDone]);

  // Poll Justice analysis status for uploaded analyzable files (auto-analyze runs server-side on upload).
  useEffect(() => {
    const pending = rows.filter((r) => r.evId && r.analyzable && r.analysis !== "analyzed");
    if (pending.length === 0) return;
    let stop = false;
    let ticks = 0;
    const tick = async () => {
      if (stop) return;
      ticks += 1;
      try {
        const ids = pending.map((r) => r.evId);
        const { data } = await api.post("/evidence/analysis-status", { ids });
        const s = data.statuses || {};
        setRows((prev) => prev.map((r) => {
          if (!r.evId || !r.analyzable) return r;
          const st = s[r.evId];
          if (st === "done") return { ...r, analysis: "analyzed" };
          if (st === "not_applicable") return { ...r, analysis: null, analyzable: false };
          return r;
        }));
      } catch { /* ignore */ }
      if (!stop && ticks < 20) setTimeout(tick, 3000);
    };
    const t = setTimeout(tick, 2500);
    return () => { stop = true; clearTimeout(t); };
  }, [rows]);

  // Poll server-side ZIP extraction jobs (archives extract in the background).
  useEffect(() => {
    const importing = rows.filter((r) => r.isArchive && r.jobId && r.status === "importing");
    if (importing.length === 0) return;
    let stop = false;
    const tick = async () => {
      if (stop) return;
      await Promise.all(importing.map(async (r) => {
        try {
          const { data } = await api.get(`/evidence/import-jobs/${r.jobId}`);
          setRows((prev) => prev.map((x) => {
            if (x.id !== r.id) return x;
            const finished = data.status === "completed" || data.status === "failed";
            return {
              ...x,
              status: finished ? (data.status === "failed" ? "error" : "done") : "importing",
              error: data.status === "failed" ? (data.error || "Import failed") : x.error,
              pct: data.percent ?? x.pct,
              archive: {
                discovered: data.discovered || 0, processed: data.processed || 0,
                added: data.added || 0, unsupported: data.unsupported || 0,
                failed: data.failed || 0, need_review: data.need_review || 0,
                percent: data.percent || 0, status: data.status,
              },
            };
          }));
        } catch { /* ignore */ }
      }));
      if (!stop) setTimeout(tick, 1500);
    };
    const t = setTimeout(tick, 1200);
    return () => { stop = true; clearTimeout(t); };
  }, [rows]);

  const startAll = () => upload(rows.filter((r) => r.status === "queued" || r.status === "error"));
  const retry = (row) => upload([row]);
  const retryAllFailed = () => upload(rows.filter((r) => r.status === "error"));

  const done = rows.filter((r) => r.status === "done").length;
  const failed = rows.filter((r) => r.status === "error").length;
  const pending = rows.filter((r) => r.status === "queued").length;
  const overall = rows.length ? Math.round((done / rows.length) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "var(--ek-page)" }} data-testid="bulk-import">
      {/* Header */}
      <div className="flex items-center justify-between px-5 sm:px-8 py-4 border-b" style={{ borderColor: "var(--ek-border)", background: "var(--ek-surface)" }}>
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ background: "var(--ek-primary)" }}>
            <UploadCloud className="h-5 w-5 text-white" />
          </span>
          <div>
            <h1 className="font-head font-bold text-lg tracking-tight">Bulk Case Import</h1>
            <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>Drop hundreds of files — {CONCURRENCY} upload at a time, large files auto-chunked</p>
          </div>
        </div>
        <button onClick={onClose} data-testid="bulk-import-close" className="h-9 w-9 rounded-md flex items-center justify-center hover:bg-white/5" style={{ color: "var(--ek-text-secondary)" }}>
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-6 max-w-3xl w-full mx-auto space-y-5">
        {/* Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="bulk-dropzone"
          className="border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors"
          style={{ borderColor: dragOver ? "var(--ek-primary)" : "var(--ek-border)", background: dragOver ? "rgba(46,130,246,0.06)" : "var(--ek-surface)" }}
        >
          <UploadCloud className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--ek-primary)" }} strokeWidth={1.4} />
          <p className="font-head font-semibold">Drag & drop files here</p>
          <p className="text-sm mt-1" style={{ color: "var(--ek-text-muted)" }}>or click to browse · photos, documents, audio & video · up to 200MB each</p>
          <input ref={inputRef} type="file" multiple className="hidden" data-testid="bulk-file-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {/* Controls */}
        {rows.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-3" data-testid="bulk-controls">
            <div className="flex items-center gap-3 text-sm" style={{ color: "var(--ek-text-secondary)" }}>
              <span data-testid="bulk-progress">{done}/{rows.length} done</span>
              {failed > 0 && <span style={{ color: "var(--ek-warning)" }}>{failed} failed</span>}
              <div className="w-40 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--ek-border)" }}>
                <div className="h-full transition-all" style={{ width: `${overall}%`, background: "var(--ek-secure)" }} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {failed > 0 && !running && (
                <Button variant="outline" size="sm" onClick={retryAllFailed} data-testid="bulk-retry-failed" className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Retry failed
                </Button>
              )}
              {done > 0 && !running && (
                <Button variant="outline" size="sm" onClick={clearDone} className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                  Clear completed
                </Button>
              )}
              <Button size="sm" onClick={startAll} disabled={running || (pending === 0 && failed === 0)} data-testid="bulk-start" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                {running ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…</> : `Start import${pending ? ` (${pending})` : ""}`}
              </Button>
            </div>
          </div>
        )}

        {/* Queue */}
        <div className="space-y-2" data-testid="bulk-queue">
          {rows.map((r) => {
            const Icon = iconFor(r.file);
            return (
              <div key={r.id} className="flex items-center gap-3 border rounded-lg px-3 py-2.5" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid={`bulk-row-${r.id}`}>
                <Icon className="h-5 w-5 shrink-0" style={{ color: "var(--ek-text-muted)" }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm truncate" data-private>{r.file.name}</span>
                    <span className="ek-mono-label shrink-0">{humanSize(r.file.size)}</span>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: "var(--ek-border)" }}>
                    <div className="h-full transition-all" style={{
                      width: `${r.status === "done" ? 100 : r.pct}%`,
                      background: r.status === "error" ? "var(--ek-danger, #ef4444)" : r.status === "done" ? "var(--ek-secure)" : "var(--ek-primary)",
                    }} />
                  </div>
                  {r.status === "error" && <p className="text-xs mt-1" style={{ color: "var(--ek-warning)" }} data-testid={`bulk-error-${r.id}`}>{r.error}</p>}
                  {r.isArchive && r.archive && r.status !== "error" && (
                    <p className="text-xs mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`bulk-archive-${r.id}`} style={{ color: r.status === "done" ? "var(--ek-secure)" : "var(--ek-text-muted)" }}>
                      {r.status === "done"
                        ? <><CheckCircle2 className="h-3 w-3" /> Extracted {r.archive.added} file{r.archive.added === 1 ? "" : "s"}
                            {r.archive.need_review > 0 && <span style={{ color: "var(--ek-warning)" }}>· {r.archive.need_review} need date review</span>}
                            {r.archive.unsupported > 0 && <span>· {r.archive.unsupported} skipped</span>}
                            {r.archive.failed > 0 && <span style={{ color: "var(--ek-warning)" }}>· {r.archive.failed} failed</span>}</>
                        : <><Loader2 className="h-3 w-3 animate-spin" /> Extracting archive… {r.archive.processed}/{r.archive.discovered || "?"} files</>}
                    </p>
                  )}
                  {r.status === "done" && r.analyzable && (
                    <p className="text-xs mt-1 flex items-center gap-1" data-testid={`bulk-analysis-${r.id}`} style={{ color: r.analysis === "analyzed" ? "var(--ek-secure)" : "var(--ek-text-muted)" }}>
                      {r.analysis === "analyzed"
                        ? <><Sparkles className="h-3 w-3" /> Analyzed by Justice</>
                        : <><Loader2 className="h-3 w-3 animate-spin" /> Analyzing…</>}
                    </p>
                  )}
                </div>
                <div className="shrink-0 w-16 text-right">
                  {r.status === "done" && <CheckCircle2 className="h-5 w-5 ml-auto" style={{ color: "var(--ek-secure)" }} data-testid={`bulk-done-${r.id}`} />}
                  {r.status === "importing" && <Loader2 className="h-4 w-4 ml-auto animate-spin" style={{ color: "var(--ek-primary)" }} />}
                  {r.status === "uploading" && <span className="ek-mono-label">{r.pct}%</span>}
                  {r.status === "queued" && (
                    <button onClick={() => removeRow(r.id)} className="opacity-60 hover:opacity-100" data-testid={`bulk-remove-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  )}
                  {r.status === "error" && (
                    <button onClick={() => retry(r)} className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--ek-primary)" }} data-testid={`bulk-retry-${r.id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Retry
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <p className="text-sm text-center py-8" style={{ color: "var(--ek-text-muted)" }}>No files queued yet.</p>
          )}
        </div>

        {done > 0 && !running && pending === 0 && failed === 0 && !rows.some((r) => r.status === "importing" || r.status === "uploading") && (
          <div className="flex items-center gap-2 rounded-md border p-3 text-sm" style={{ borderColor: "rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.08)", color: "var(--ek-secure)" }} data-testid="bulk-complete">
            <CheckCircle2 className="h-4 w-4" /> All {done} files imported to this case.
          </div>
        )}
      </div>
    </div>
  );
}
